import React, {useRef} from 'react';
import style from './index.module.scss';
import {isTrue} from 'Utils/common';
import {deletePlan, getPlanPage, PlanListItem, Status, User} from '../service';
import {Button, message, Space} from 'antd';
import useSelection from '../../../Utils/Hooks/useSelection';
import MultiLineColumn from '../Components/MultiLineColumn';
import dayjs from 'dayjs';
import {SortOrder} from 'antd/es/table/interface';
import OverLengthColumn from '../Components/OverLengthColumn';
import useTable from '../../../Utils/Hooks/useTable';
import TableRender, {ProColumnsType, TableContext} from 'table-render';
import {useMutation} from 'react-query';
import useModalHandler from '../../../Utils/Hooks/useModalHandler';
import AddPlanModal from './components/AddPlanModal';
import Operations from './components/Operations';
import DeleteConfirm from '../../../Components/DeleteConfirm';

export interface searchItem {
    name?: string; //名称及编码
    status?: Status; //启用状态
}

export default function TaskList() {
    const {tableInit} = useTable(getPlanPage, {});
    const rowSelection = useSelection();
    const tableRef = useRef<TableContext>(null);

    function reload() {
        tableRef?.current?.refresh({stay: true});
    }
    const sortDirections: SortOrder[] = ['descend', 'ascend'];
    const columns: ProColumnsType<PlanListItem> = [
        {
            title: '计划名称及编码',
            dataIndex: 'code',
            align: 'left',
            render: (text: string, record: PlanListItem) => (
                <MultiLineColumn name={record.name} no={text} />
            ),
        },
        {
            title: '启用状态',
            dataIndex: 'status',
            align: 'center',
            render: (text: Status) => (
                <div>{isTrue(text) ? '已启用' : '已停用'}</div>
            ),
        },
        {
            title: '创建人',
            dataIndex: 'createUser',
            align: 'center',
            render: (text: User) => (
                <MultiLineColumn name={text.name} no={text.loginName} />
            ),
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            align: 'center',
            sorter: (a, b) => dayjs(a.createTime).diff(b.createTime),
            render: (text: string) => (
                <div>
                    {text ? dayjs(text).format('YY-MM-DD HH:mm:ss') : '--'}
                </div>
            ),
        },
        {
            title: '时间表达式',
            dataIndex: 'cron',
            align: 'center',
        },
        {
            title: '有效开始日',
            dataIndex: 'startDate',
            align: 'center',
            render: (text: string) => (
                <div>{text ? dayjs(text).format('YY-MM-DD') : '--'}</div>
            ),
        },
        {
            title: '有效结束日',
            dataIndex: 'endDate',
            align: 'center',
            render: (text: string) => (
                <div>{text ? dayjs(text).format('YY-MM-DD') : '--'}</div>
            ),
        },
        {
            title: '有效状态',
            dataIndex: 'youxiaoState',
            align: 'center',
        },
        {
            title: '更新人',
            dataIndex: 'updateUser',
            align: 'center',
            render: (text: User) => (
                <MultiLineColumn name={text.name} no={text.loginName} />
            ),
        },
        {
            title: '更新时间',
            dataIndex: 'updateTime',
            align: 'center',
            sortDirections: sortDirections,
            sorter: (a, b) => dayjs(a.updateTime).diff(b.updateTime),
            render: (text: string) => (
                <div>
                    {text ? dayjs(text).format('YY-MM-DD HH:mm:ss') : '--'}
                </div>
            ),
        },
        {
            title: '说明',
            dataIndex: 'description',
            align: 'center',
            render: (text: string) => <OverLengthColumn value={text} />,
        },
        {
            title: '操作',
            width: 250,
            align: 'center',
            fixed: 'right',
            render: (value, record) => (
                <Operations record={record} updateData={() => reload()} />
            ),
        },
    ];

    const schema = {
        type: 'object',
        labelWidth: 130,
        properties: {
            name: {
                title: '名称及编号',
                type: 'string',
            },
            status: {
                title: '启用状态',
                type: 'string',
                widget: 'select',
                props: {
                    options: [
                        {label: '全部', value: ''},
                        {label: '已启用', value: Status.Open},
                        {label: '已停用', value: Status.Close},
                    ],
                },
            },
        },
    };

    const onDelete = useMutation({
        mutationFn: deletePlan,
        onSuccess: () => {
            message.success('成功!');
            reload();
            rowSelection.clear();
        },
    });
    const addModal = useModalHandler({
        component: AddPlanModal,
    });
    const toolBar = (
        <Space>
            <Button
                type="primary"
                onClick={() =>
                    addModal.open({
                        flag: 'add',
                        refreshData: () => {
                            addModal.close();
                            reload();
                        },
                    })
                }>
                新增调度计划
            </Button>
            <DeleteConfirm
                onSuccess={() => onDelete.mutate(rowSelection.selected.join())}>
                <Button disabled={rowSelection.isEmpty} danger ghost>
                    批量删除
                </Button>
            </DeleteConfirm>
        </Space>
    );

    return (
        <div className={style.root}>
            <TableRender
                columns={columns}
                rowKey="id"
                toolbarRender={toolBar}
                rowSelection={rowSelection}
                search={{schema}}
                ref={tableRef}
                {...tableInit()}
            />
            {addModal.render()}
        </div>
    );
}
